import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import { SurveyData } from '../App';
import { getCurrentUser } from '../auth/LoginButton';
import { PendingDashboardNavContext } from '../App';

interface SurveyListProps {
  onCreateNew: () => void;
  onEditSurvey: (surveyId: string) => void;
  onViewResponses: (surveyId: string) => void;
}

const SurveyList: React.FC<SurveyListProps> = ({ onCreateNew, onEditSurvey, onViewResponses }) => {
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState<SurveyData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showUrlModal, setShowUrlModal] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState<SurveyData | null>(null);
  const [currentUserEmail, setCurrentUserEmail] = useState<string>('');
  const [mySurveysExpanded, setMySurveysExpanded] = useState(true);
  const [allSurveysExpanded, setAllSurveysExpanded] = useState(true);
  const [showDeletedSurveys, setShowDeletedSurveys] = useState(false);
  const currentUser = getCurrentUser();

  console.log('SurveyList loaded with onViewResponses:', typeof onViewResponses);

  useEffect(() => {
    // Get current user email
    const user = sessionStorage.getItem('user');
    if (user) {
      const userData = JSON.parse(user);
      setCurrentUserEmail(userData.email || '');
    }
    fetchSurveys();
  }, []);

  const fetchSurveys = async () => {
    try {
      setLoading(true);
      console.log('📋 Fetching surveys from /surveys?include_deleted=true');
      const response = await axios.get('/surveys?include_deleted=true');
      console.log('📋 Response received:', response);
      console.log('📋 Response data type:', typeof response.data);
      console.log('📋 Response data is array:', Array.isArray(response.data));
      
      // Defensive check - ensure we have an array
      if (Array.isArray(response.data)) {
        console.log('📋 Setting surveys, count:', response.data.length);
        setSurveys(response.data);
        setError(null);
      } else if (response.data && response.data.error) {
        console.error('📋 API returned error:', response.data.error);
        setError(response.data.error);
        setSurveys([]);
      } else {
        console.error('📋 Unexpected response format:', response.data);
        setError('Unexpected response format from server');
        setSurveys([]);
      }
    } catch (err: any) {
      console.error('📋 Error fetching surveys:', err);
      console.error('📋 Error response:', err.response);
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to load surveys');
      setSurveys([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (surveyId: string) => {
    if (!confirm('Are you sure you want to delete this survey?')) return;

    try {
      await axios.delete(`/surveys/${surveyId}`);
      setSurveys(surveys.filter(s => s.id !== surveyId));
    } catch (err: any) {
      alert('Failed to delete survey: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleTogglePublish = async (survey: SurveyData) => {
    try {
      // If survey is deleted, restore it when publishing
      const updateData: any = {
        is_published: !survey.is_published
      };
      
      if (survey.is_deleted && !survey.is_published) {
        // Publishing a deleted survey restores it
        updateData.is_deleted = false;
      }
      
      await axios.put(`/surveys/${survey.id}`, updateData);
      
      setSurveys(surveys.map(s => 
        s.id === survey.id ? { ...s, is_published: !s.is_published, is_deleted: updateData.is_deleted !== undefined ? updateData.is_deleted : s.is_deleted } : s
      ));
      
      // Show success message if restored
      if (updateData.is_deleted === false) {
        alert('✅ Survey restored and published successfully!');
      }
    } catch (err: any) {
      alert('Failed to update survey: ' + (err.response?.data?.message || err.message));
    }
  };

  const { setPendingDashboardNav } = useContext(PendingDashboardNavContext) as { setPendingDashboardNav: (val: boolean) => void };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <div style={{ fontSize: '1.2rem', color: 'var(--text-secondary)' }}>Loading surveys...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <div style={{ fontSize: '1.2rem', color: 'var(--error)', marginBottom: '1rem' }}>❌ Error loading surveys</div>
        <div style={{ fontSize: '1rem', color: 'var(--text-secondary)' }}>{error}</div>
        <button 
          onClick={fetchSurveys}
          style={{
            marginTop: '1rem',
            padding: '0.5rem 1rem',
            backgroundColor: 'var(--button-bg)',
            color: 'var(--button-text)',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  // Filter surveys - exclude deleted surveys from main views
  const activeSurveys = surveys.filter(s => !s.is_deleted);
  const deletedSurveys = surveys.filter(s => s.is_deleted);
  const mySurveys = activeSurveys.filter(s => s.creator_id === currentUserEmail);
  const allSurveys = activeSurveys;

  const renderSurveyCard = (survey: SurveyData) => (
    <div 
      key={survey.id}
      style={{
        background: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        borderRadius: '12px',
        padding: '1.5rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        transition: 'all 0.2s'
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
        e.currentTarget.style.borderColor = '#667eea';
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
        e.currentTarget.style.borderColor = '#e0e0e0';
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
        <div style={{ flex: 1 }}>
          <h3 
            onClick={() => {
              setSelectedSurvey(survey);
              setShowUrlModal(true);
            }}
            style={{ 
              margin: '0 0 0.5rem', 
              fontSize: '1.3rem', 
              color: '#667eea',
              cursor: 'pointer',
              textDecoration: 'underline',
              textDecorationStyle: 'dotted',
              textUnderlineOffset: '4px'
            }}
            onMouseOver={(e) => e.currentTarget.style.color = '#5568d3'}
            onMouseOut={(e) => e.currentTarget.style.color = '#667eea'}
          >
            {survey.title}
          </h3>
          {survey.description && (
            <p style={{ margin: '0 0 1rem', color: 'var(--text-secondary)' }}>{survey.description}</p>
          )}
          <div style={{ display: 'flex', gap: '1rem', fontSize: '0.9rem', color: 'var(--text-secondary)', flexWrap: 'wrap' }}>
            <span>
              <strong>{survey.question_count}</strong> questions
            </span>
            <span>•</span>
            <span>
              Owner: <strong style={{ color: 'var(--accent)' }}>{survey.creator_name || survey.creator_id}</strong>
            </span>
            <span>•</span>
            <span>
              Created: {(() => {
                const dateStr = survey.createdAt || survey.created_at;
                if (!dateStr) return 'N/A';
                try {
                  const date = new Date(dateStr);
                  return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString();
                } catch {
                  return 'N/A';
                }
              })()}
            </span>
            <span>•</span>
            <span style={{ 
              color: survey.is_published ? '#22c55e' : '#f59e0b',
              fontWeight: 'bold'
            }}>
              {survey.is_published ? '✓ Published' : '○ Draft'}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', marginLeft: '1rem' }}>
          <button
            onClick={() => onViewResponses(survey.id)}
            style={{
              background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
              color: 'var(--button-text)',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              fontSize: '0.9rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            title="View Responses"
          >
            📊 Responses
          </button>
          <button
            onClick={() => onEditSurvey(survey.id)}
            style={{
              background: '#667eea',
              color: 'white',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              fontSize: '0.9rem',
              cursor: 'pointer'
            }}
            title="Edit Survey"
          >
            ✏️ Edit
          </button>
          <button
            onClick={() => handleTogglePublish(survey)}
            style={{
              background: survey.is_published ? '#f59e0b' : '#22c55e',
              color: 'white',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              fontSize: '0.9rem',
              cursor: 'pointer'
            }}
            title={survey.is_published ? 'Unpublish' : 'Publish'}
          >
            {survey.is_published ? '○ Unpublish' : '✓ Publish'}
          </button>
          <button
            onClick={() => handleDelete(survey.id)}
            style={{
              background: '#ef4444',
              color: 'white',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              fontSize: '0.9rem',
              cursor: 'pointer'
            }}
            title="Delete Survey"
          >
            🗑️ Delete
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '2rem'
      }}>
        <h1 style={{ margin: 0, fontSize: '2rem' }}>Surveys</h1>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <button 
            onClick={fetchSurveys}
            style={{
              background: 'var(--button-bg)',
              color: 'var(--button-text)',
              border: '1px solid var(--card-border)',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'var(--accent-hover)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'var(--button-bg)';
            }}
          >
            🔄 Refresh
          </button>
          {currentUser?.role === 'Survey.Admin' && (
            <button
              onClick={() => setShowDeletedSurveys(!showDeletedSurveys)}
              style={{
                background: showDeletedSurveys ? '#f44336' : '#666',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = showDeletedSurveys ? '#d32f2f' : '#555';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = showDeletedSurveys ? '#f44336' : '#666';
              }}
            >
              {showDeletedSurveys ? '← Back to Surveys' : '🗑️ Deleted Surveys'}
            </button>
          )}
          <button 
            onClick={onCreateNew}
            style={{
              background: '#667eea',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.background = '#5568d3'}
            onMouseOut={(e) => e.currentTarget.style.background = '#667eea'}
          >
            + Create New Survey
          </button>
          
          {/* Campaign Dashboard button - only for Survey.Admin and Campaign.Manager
              (Survey.Creator and Survey.Agent are intentionally excluded from the ECM/Campaign Dashboard). */}
          {(currentUser?.role === 'Survey.Admin' ||
            currentUser?.role === 'Campaign.Manager') && (
            <button 
              onClick={() => {
                setPendingDashboardNav(true);
                navigate('/ECM/Dashboard', { replace: true });
              }}
              disabled={false}
              style={{
                background: '#f59e0b',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                transition: 'all 0.2s',
                opacity: 1
              }}
              onMouseOver={(e) => e.currentTarget.style.background = '#d97706'}
              onMouseOut={(e) => e.currentTarget.style.background = '#f59e0b'}
            >
              📧 Campaign Dashboard
            </button>
          )}
        </div>
      </div>

      {error && (
        <div style={{
          background: '#fee',
          border: '1px solid #fcc',
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '1rem',
          color: 'var(--error)'
        }}>
          {error}
        </div>
      )}

      {/* Deleted Surveys Section - Only for Survey Admins */}
      {showDeletedSurveys && currentUser?.role === 'Survey.Admin' && (
        <div style={{ marginBottom: '2rem' }}>
          {/* Header with gradient using theme colors */}
          <div style={{
            background: 'linear-gradient(135deg, var(--accent) 0%, var(--accent-hover) 100%)',
            padding: '2rem',
            borderRadius: '16px',
            marginBottom: '2rem',
            boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
            border: '1px solid var(--card-border)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h2 style={{ 
                  margin: '0 0 0.5rem', 
                  fontSize: '2rem', 
                  color: 'var(--button-text)',
                  fontWeight: 'bold',
                  textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}>
                  🗑️ Deleted Surveys
                </h2>
                <p style={{ 
                  margin: 0, 
                  color: 'var(--button-text)', 
                  fontSize: '1rem',
                  opacity: 0.9
                }}>
                  {deletedSurveys.length} survey{deletedSurveys.length !== 1 ? 's' : ''} in trash • Click "Restore" to recover
                </p>
              </div>
              <div style={{
                background: 'var(--card-bg)',
                backdropFilter: 'blur(10px)',
                padding: '1rem 2rem',
                borderRadius: '12px',
                border: '1px solid var(--card-border)'
              }}>
                <div style={{ fontSize: '3rem', fontWeight: 'bold', color: 'var(--text-primary)', textAlign: 'center' }}>
                  {deletedSurveys.length}
                </div>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', textAlign: 'center', marginTop: '0.25rem' }}>
                  Deleted
                </div>
              </div>
            </div>
          </div>

          {deletedSurveys.length === 0 ? (
            <div style={{
              padding: '4rem 2rem',
              textAlign: 'center',
              background: 'var(--card-bg)',
              borderRadius: '16px',
              border: '2px dashed var(--card-border)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }}>
              <div style={{ fontSize: '4rem', marginBottom: '1rem', opacity: 0.5 }}>🗑️</div>
              <div style={{ fontSize: '1.25rem', color: 'var(--text-primary)', fontWeight: '500' }}>
                No deleted surveys
              </div>
              <div style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                Deleted surveys will appear here
              </div>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '1.5rem', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))' }}>
              {deletedSurveys.map(survey => (
                <div 
                  key={survey.id}
                  style={{
                    background: 'var(--card-bg)',
                    border: '2px solid var(--accent)',
                    borderRadius: '16px',
                    padding: '1.5rem',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    transition: 'all 0.3s',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.25)';
                    e.currentTarget.style.transform = 'translateY(-4px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {/* Deleted badge overlay */}
                  <div style={{
                    position: 'absolute',
                    top: '1rem',
                    right: '1rem',
                    background: 'var(--accent)',
                    color: 'var(--button-text)',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.4)'
                  }}>
                    Deleted
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div>
                      <h3 
                        onClick={() => {
                          setSelectedSurvey(survey);
                          setShowUrlModal(true);
                        }}
                        style={{ 
                          margin: '0 0 0.5rem', 
                          fontSize: '1.3rem', 
                          color: 'var(--accent)',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          textDecorationStyle: 'dotted',
                          textUnderlineOffset: '4px',
                          paddingRight: '5rem'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.opacity = '0.8'}
                        onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
                      >
                        {survey.title}
                      </h3>
                      {survey.description && (
                        <p style={{ margin: '0 0 1rem', color: 'var(--text-secondary)', fontSize: '0.95rem' }}>
                          {survey.description}
                        </p>
                      )}
                      <div style={{ display: 'flex', gap: '0.75rem', fontSize: '0.85rem', color: 'var(--text-secondary)', flexWrap: 'wrap' }}>
                        <span style={{ background: 'var(--bg-secondary)', padding: '0.25rem 0.5rem', borderRadius: '6px', border: '1px solid var(--card-border)' }}>
                          <strong>{survey.question_count}</strong> questions
                        </span>
                        <span style={{ background: 'var(--bg-secondary)', padding: '0.25rem 0.5rem', borderRadius: '6px', border: '1px solid var(--card-border)' }}>
                          Owner: <strong>{survey.creator_name || survey.creator_id}</strong>
                        </span>
                        <span style={{ background: 'var(--bg-secondary)', padding: '0.25rem 0.5rem', borderRadius: '6px', border: '1px solid var(--card-border)' }}>
                          {(() => {
                            const dateStr = survey.createdAt || survey.created_at;
                            if (!dateStr) return 'N/A';
                            try {
                              const date = new Date(dateStr);
                              return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString();
                            } catch {
                              return 'N/A';
                            }
                          })()}
                        </span>
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', borderTop: '1px solid var(--card-border)', paddingTop: '1rem' }}>
                      <button
                        onClick={() => handleTogglePublish(survey)}
                        style={{
                          flex: 1,
                          background: 'var(--success)',
                          color: 'var(--button-text)',
                          border: 'none',
                          padding: '0.75rem 1rem',
                          borderRadius: '10px',
                          fontSize: '0.95rem',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                          transition: 'all 0.2s',
                          minWidth: '120px'
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.transform = 'scale(1.05)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.4)';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.3)';
                        }}
                        title="Restore and publish this survey"
                      >
                        ♻️ Restore & Publish
                      </button>
                      <button
                        onClick={() => onViewResponses(survey.id)}
                        style={{
                          background: 'var(--warning)',
                          color: 'var(--button-text)',
                          border: 'none',
                          padding: '0.75rem 1rem',
                          borderRadius: '10px',
                          fontSize: '0.9rem',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                          transition: 'all 0.2s',
                          minWidth: '100px'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.4)'}
                        onMouseOut={(e) => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.3)'}
                        title="View responses"
                      >
                        📊 Responses
                      </button>
                      <button
                        onClick={() => {
                          if (confirm('⚠️ Permanently delete this survey? This cannot be undone!')) {
                            handleDelete(survey.id);
                          }
                        }}
                        style={{
                          background: 'var(--warning)',
                          color: 'var(--button-text)',
                          border: 'none',
                          padding: '0.75rem 1rem',
                          borderRadius: '10px',
                          fontSize: '0.9rem',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                          transition: 'all 0.2s',
                          minWidth: '100px'
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.5)';
                          e.currentTarget.style.transform = 'scale(1.05)';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.3)';
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                        title="Permanently delete"
                      >
                        🗑️ Delete Forever
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Active Surveys Sections - Hidden when viewing deleted */}
      {!showDeletedSurveys && (
        <>
      {/* My Surveys Section */}
      <div style={{ marginBottom: '3rem' }}>
        <h2 
          onClick={() => setMySurveysExpanded(!mySurveysExpanded)}
          style={{ 
            margin: '0 0 1rem', 
            fontSize: '1.5rem', 
            color: '#667eea',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            userSelect: 'none'
          }}
        >
          <span style={{ fontSize: '1rem' }}>{mySurveysExpanded ? '▼' : '▶'}</span>
          My Surveys
        </h2>
        {mySurveysExpanded && (
          mySurveys.length === 0 ? (
            <div style={{
              background: '#f9f9f9',
              border: '2px dashed #ddd',
              borderRadius: '12px',
              padding: '2rem',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📋</div>
              <p style={{ color: 'var(--text-secondary)', margin: 0 }}>You haven't created any surveys yet</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '1rem' }}>
              {mySurveys.map(renderSurveyCard)}
            </div>
          )
        )}
      </div>

      {/* All Surveys Section */}
      <div>
        <h2 
          onClick={() => setAllSurveysExpanded(!allSurveysExpanded)}
          style={{ 
            margin: '0 0 1rem', 
            fontSize: '1.5rem', 
            color: '#667eea',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            userSelect: 'none'
          }}
        >
          <span style={{ fontSize: '1rem' }}>{allSurveysExpanded ? '▼' : '▶'}</span>
          All Surveys
        </h2>
        {allSurveysExpanded && (
          allSurveys.length === 0 ? (
            <div style={{
              background: '#f9f9f9',
              border: '2px dashed #ddd',
              borderRadius: '12px',
              padding: '3rem',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📋</div>
              <h3 style={{ color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>No surveys yet</h3>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>Create your first survey to get started</p>
              <button 
                onClick={onCreateNew}
                style={{
                  background: '#667eea',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 2rem',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: 'pointer'
                }}
              >
                Create Survey
              </button>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '1rem' }}>
              {allSurveys.map(renderSurveyCard)}
            </div>
          )
        )}
      </div>

      {/* URL Modal */}
      {showUrlModal && selectedSurvey && (
        <div
          onClick={() => setShowUrlModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--card-bg)',
              borderRadius: '12px',
              padding: '2rem',
              maxWidth: '600px',
              width: '90%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
          >
            <h3 style={{ margin: '0 0 1rem', fontSize: '1.5rem', color: 'var(--text-primary)' }}>
              Survey Public URL
            </h3>
            <p style={{ margin: '0 0 1.5rem', color: 'var(--text-secondary)' }}>
              Share this link with participants to take the survey:
            </p>
            <div style={{
              background: '#f8f9fa',
              border: '2px solid #667eea',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1.5rem'
            }}>
              <code style={{
                fontSize: '1rem',
                color: '#667eea',
                wordBreak: 'break-all',
                fontWeight: '600'
              }}>
                {selectedSurvey.url || `${import.meta.env.VITE_PUBLIC_SURVEY_URL || 'http://localhost:3001'}/survey/${selectedSurvey.slug}`}
              </code>
            </div>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  const url = selectedSurvey.url || `${import.meta.env.VITE_PUBLIC_SURVEY_URL || 'http://localhost:3001'}/survey/${selectedSurvey.slug}`;
                  navigator.clipboard.writeText(url);
                  const btn = document.activeElement as HTMLButtonElement;
                  const originalText = btn.textContent;
                  btn.textContent = '✓ Copied!';
                  setTimeout(() => {
                    btn.textContent = originalText;
                  }, 2000);
                }}
                style={{
                  background: '#667eea',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.background = '#5568d3'}
                onMouseOut={(e) => e.currentTarget.style.background = '#667eea'}
              >
                📋 Copy URL
              </button>
              <button
                onClick={() => setShowUrlModal(false)}
                style={{
                  background: '#f3f4f6',
                  color: 'var(--text-primary)',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.background = '#e5e7eb'}
                onMouseOut={(e) => e.currentTarget.style.background = '#f3f4f6'}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default SurveyList;
